import React, { useState, useEffect } from 'react';
import { useApi } from '../hooks/useApi.js';
import { useAuth } from '../context/AuthContext.jsx';

// Display order: Mon first (index 1..6, then 0)
const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAY_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? 'pm' : 'am';
  const h12 = hour % 12 || 12;
  return m === '00' ? `${h12}${ampm}` : `${h12}:${m}${ampm}`;
}

export default function AvailabilityPage() {
  const api = useApi();
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';

  const [data, setData] = useState({ recurring: [], overrides: [] });
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modal state for setting availability
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('recurring'); // 'recurring' | 'override'
  const [form, setForm] = useState({
    employee_id: '',
    days_of_week: [1],
    start_time: '09:00',
    end_time: '17:00',
    date: '',
    is_available: true,
    reason: '',
  });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    try {
      if (isOwner) {
        const [avail, emps] = await Promise.all([
          api.get('/availability'),
          api.get('/employees'),
        ]);
        setData(avail);
        setEmployees(emps.filter(e => e.is_active));
      } else {
        const avail = await api.get('/availability/my');
        setData(avail);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function openAddModal(type, dayOfWeek) {
    setModalType(type);
    setForm(prev => ({
      ...prev,
      days_of_week: dayOfWeek != null ? [dayOfWeek] : [1],
      date: '',
      employee_id: isOwner ? (employees[0]?.id || '') : '',
    }));
    setFormError('');
    setShowModal(true);
  }

  async function handleSave(e) {
    e.preventDefault();
    setFormError('');
    setSaving(true);

    try {
      if (modalType === 'recurring') {
        if (form.days_of_week.length === 0) { setFormError('Select at least one day.'); setSaving(false); return; }
        await Promise.all(
          form.days_of_week.map(day => {
            const payload = { day_of_week: day, start_time: form.start_time, end_time: form.end_time };
            if (isOwner) payload.employee_id = form.employee_id;
            return api.post('/availability', payload);
          })
        );
      } else {
        if (!form.date) { setFormError('Date is required.'); setSaving(false); return; }
        const payload = {
          date: form.date,
          is_available: form.is_available === 'true' || form.is_available === true,
          start_time: form.is_available ? form.start_time : undefined,
          end_time: form.is_available ? form.end_time : undefined,
          reason: form.reason || undefined,
        };
        if (isOwner) payload.employee_id = form.employee_id;
        await api.post('/availability/override', payload);
      }
      setShowModal(false);
      await load();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id, type) {
    if (!window.confirm('Remove this availability entry?')) return;
    try {
      if (type === 'override') {
        await api.delete(`/availability/override/${id}`);
      } else {
        await api.delete(`/availability/${id}`);
      }
      await load();
    } catch (err) {
      alert(err.message);
    }
  }

  // Group recurring by employee for owner view
  function buildOwnerGrid() {
    const byEmployee = {};
    for (const a of data.recurring) {
      if (!byEmployee[a.employee_id]) {
        byEmployee[a.employee_id] = { name: a.employee_name, role: a.employee_role, days: {} };
      }
      byEmployee[a.employee_id].days[a.day_of_week] = a;
    }
    return Object.values(byEmployee);
  }

  // Build own recurring map for staff view
  function buildStaffGrid() {
    const days = {};
    for (const a of data.recurring) {
      days[a.day_of_week] = a;
    }
    return days;
  }

  if (loading) return (
    <div className="container">
      <div style={{ textAlign: 'center', padding: 64 }}>
        <span className="spinner spinner-dark" style={{ width: 28, height: 28 }} />
      </div>
    </div>
  );

  return (
    <div className="container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Availability</h1>
          <p className="page-subtitle">
            {isOwner ? 'Recurring weekly availability for all staff' : 'Your weekly availability'}
          </p>
        </div>
        {isOwner && (
          <div className="flex gap-2 availability-actions">
            <button className="btn btn-secondary" onClick={() => openAddModal('override')}>
              Add Date Override
            </button>
            <button className="btn btn-primary" onClick={() => openAddModal('recurring')}>
              Set Weekly Availability
            </button>
          </div>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Recurring availability grid */}
      <div className="card mb-4">
        <div className="card-header">
          <span className="card-title">Weekly Recurring Availability</span>
        </div>
        <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
          {isOwner ? (
            <>
              {/* Desktop grid */}
              <div className="avail-grid avail-desktop-only">
                <div className="avail-header">Employee</div>
                {DAYS.map((d, i) => <div key={i} className="avail-header">{d}</div>)}
                {buildOwnerGrid().length === 0 ? (
                  <div style={{ gridColumn: '1 / -1', padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>
                    No availability set yet
                  </div>
                ) : buildOwnerGrid().map(emp => (
                  <React.Fragment key={emp.name}>
                    <div className="avail-name">
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{emp.name}</div>
                        <div className="text-muted text-sm">{emp.role?.replace(/_/g, ' ')}</div>
                      </div>
                    </div>
                    {DAY_ORDER.map(dayIdx => {
                      const a = emp.days[dayIdx];
                      return (
                        <div key={dayIdx} className="avail-cell">
                          {a ? (
                            <div className="avail-available">{formatTime(a.start_time)}–{formatTime(a.end_time)}</div>
                          ) : (
                            <div className="avail-unavailable">N/A</div>
                          )}
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>

              {/* Mobile: card per employee */}
              <div className="avail-mobile-list">
                {buildOwnerGrid().length === 0 ? (
                  <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>No availability set yet</div>
                ) : buildOwnerGrid().map(emp => (
                  <div key={emp.name} className="avail-mobile-card">
                    <div className="avail-mobile-emp-header">
                      <strong>{emp.name}</strong>
                      <span className="text-muted text-sm" style={{ textTransform: 'capitalize' }}>{emp.role?.replace(/_/g, ' ')}</span>
                    </div>
                    <div className="avail-mobile-days">
                      {DAY_ORDER.map((dayIdx, i) => {
                        const a = emp.days[dayIdx];
                        return (
                          <div key={dayIdx} className={`avail-mobile-day-cell${a ? ' avail-mobile-avail' : ' avail-mobile-na'}`}>
                            <div className="avail-mobile-day-name">{DAYS[i]}</div>
                            <div className="avail-mobile-day-time">
                              {a ? `${formatTime(a.start_time)}–${formatTime(a.end_time)}` : 'N/A'}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            // Staff: own weekly grid
            <>
              <div className="avail-grid avail-desktop-only" style={{ gridTemplateColumns: 'repeat(7, 1fr)' }}>
                {DAYS.map((d, i) => <div key={i} className="avail-header">{d}</div>)}
                {(() => {
                  const grid = buildStaffGrid();
                  return DAY_ORDER.map(dayIdx => {
                    const a = grid[dayIdx];
                    return (
                      <div key={dayIdx} className="avail-cell" style={{ minHeight: 60 }}>
                        {a ? (
                          <div className="avail-available">
                            {formatTime(a.start_time)}–{formatTime(a.end_time)}
                          </div>
                        ) : (
                          <div className="avail-unavailable">N/A</div>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
              <div className="avail-mobile-list" style={{ padding: '12px 16px' }}>
                <div className="avail-mobile-days">
                  {(() => {
                    const grid = buildStaffGrid();
                    return DAY_ORDER.map((dayIdx, i) => {
                      const a = grid[dayIdx];
                      return (
                        <div key={dayIdx} className={`avail-mobile-day-cell${a ? ' avail-mobile-avail' : ' avail-mobile-na'}`}>
                          <div className="avail-mobile-day-name">{DAYS[i]}</div>
                          <div className="avail-mobile-day-time">
                            {a ? `${formatTime(a.start_time)}–${formatTime(a.end_time)}` : 'N/A'}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Date overrides */}
      {data.overrides.length > 0 && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">Date-Specific Overrides</span>
          </div>
          <div className="table-wrap table-wrap-nostack" style={{ borderRadius: 0, border: 'none' }}>
            <table>
              <thead>
                <tr>
                  {isOwner && <th>Employee</th>}
                  <th>Date</th>
                  <th>Status</th>
                  <th>Times</th>
                  <th>Reason</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.overrides.map(o => (
                  <tr key={o.id}>
                    {isOwner && <td data-label="Employee">{o.employee_name}</td>}
                    <td data-label="Date">{new Date(o.date + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</td>
                    <td data-label="Status">
                      <span className={`badge badge-${o.is_available ? 'success' : 'danger'}`}>
                        {o.is_available ? 'Available' : 'Unavailable'}
                      </span>
                    </td>
                    <td data-label="Times">
                      {o.is_available && o.start_time
                        ? `${formatTime(o.start_time)} – ${formatTime(o.end_time)}`
                        : '—'}
                    </td>
                    <td data-label="Reason">{o.reason || '—'}</td>
                    <td>
                      <button className="btn btn-ghost btn-sm" style={{ color: 'var(--color-danger)' }}
                        onClick={() => handleDelete(o.id, 'override')}>
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">
                {modalType === 'recurring' ? 'Set Weekly Availability' : 'Add Date Override'}
              </span>
              <button className="modal-close" onClick={() => setShowModal(false)}>&#x2715;</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="modal-body">
                {formError && <div className="alert alert-error">{formError}</div>}

                {isOwner && (
                  <div className="form-group">
                    <label>Employee <span className="required">*</span></label>
                    <select
                      className="form-control"
                      value={form.employee_id}
                      onChange={e => setForm(p => ({ ...p, employee_id: e.target.value }))}
                      required
                    >
                      <option value="">Select employee...</option>
                      {employees.map(e => (
                        <option key={e.id} value={e.id}>{e.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {modalType === 'recurring' ? (
                  <div className="form-group">
                    <label>Days <span className="required">*</span></label>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                      {DAY_ORDER.map(i => {
                        const d = DAY_FULL[i];
                        const selected = form.days_of_week.includes(i);
                        return (
                          <button
                            key={i}
                            type="button"
                            onClick={() => setForm(p => ({
                              ...p,
                              days_of_week: selected
                                ? p.days_of_week.filter(x => x !== i)
                                : [...p.days_of_week, i].sort((a, b) => a - b),
                            }))}
                            style={{
                              padding: '8px 12px', borderRadius: 16, fontSize: 13, cursor: 'pointer', border: '1px solid',
                              borderColor: selected ? 'var(--color-primary)' : 'var(--border)',
                              background: selected ? 'var(--color-primary)' : 'transparent',
                              color: selected ? '#fff' : 'var(--text-muted)',
                              fontWeight: selected ? 600 : 400,
                              minHeight: 36,
                            }}
                          >
                            {d.slice(0, 3)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="form-group">
                      <label>Date <span className="required">*</span></label>
                      <input
                        type="date"
                        className="form-control"
                        value={form.date}
                        onChange={e => setForm(p => ({ ...p, date: e.target.value }))}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Status</label>
                      <select
                        className="form-control"
                        value={form.is_available ? 'true' : 'false'}
                        onChange={e => setForm(p => ({ ...p, is_available: e.target.value === 'true' }))}
                      >
                        <option value="true">Available</option>
                        <option value="false">Unavailable</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Reason</label>
                      <input
                        type="text"
                        className="form-control"
                        value={form.reason}
                        onChange={e => setForm(p => ({ ...p, reason: e.target.value }))}
                        placeholder="e.g. Personal appointment, holiday..."
                      />
                    </div>
                  </>
                )}

                {(modalType === 'recurring' || form.is_available) && (
                  <div className="form-row">
                    <div className="form-group">
                      <label>Available From</label>
                      <input
                        type="time"
                        className="form-control"
                        value={form.start_time}
                        onChange={e => setForm(p => ({ ...p, start_time: e.target.value }))}
                      />
                    </div>
                    <div className="form-group">
                      <label>Available Until</label>
                      <input
                        type="time"
                        className="form-control"
                        value={form.end_time}
                        onChange={e => setForm(p => ({ ...p, end_time: e.target.value }))}
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? <span className="spinner" /> : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
